import CardCommon from '@op/components/CardCommon';
import {
  ReportOwnerSegment,
  SAVED_LIST_DEFAULT_REPORT_OWNER,
  type TReportOwner,
} from '@op/components/ReportPermission';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Segment } from 'tea-component';
import './index.scss';
import SummaryListTable from '@op/pages/AITaskCenter/components/SummaryListTable';
import { useIntl, useLocation } from 'umi';
import SubscriptionsTable from '@op/pages/AITaskCenter/components/SubscriptionsSummaryListTable/SubscriptionsTable';
import { useAiTaskTabInfo } from '@op/pages/AITaskCenter/hooks';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { ILocationWithQuery, LocationState } from '../../PreviewAnalysis';

interface ISavedAnalysisList {
  gameInfo: any;
  wholeGameInfo: any;
  needRequest: boolean;
  isKeyword?: boolean;
  categoryType?: 'keyword' | 'url' | 'keyword_topic';
  showSegment?: boolean; // 是否显示 Segment 筛选器（仅在 Keyword Analysis 页面显示）
  initialSecondSegment?: string; // 从父组件传递的初始 secondSegment
  initialOwner?: TReportOwner; // 从 URL query 或 AITaskCenter 跳转带入
}

type TFirstSegment = 'keyword' | 'keyword_topic' | 'subscription';
type TabType = 'keyword' | 'url' | 'topic' | 'repeat_summary_task';

const SavedAnalysisList = ({
  gameInfo,
  needRequest: _needRequest,
  isKeyword: _isKeyword = true,
  categoryType = 'keyword',
  showSegment = false, // 默认不显示 Segment
  wholeGameInfo,
  initialSecondSegment,
  initialOwner,
}: ISavedAnalysisList) => {
  const { formatMessage } = useIntl();
  const location =
    useLocation<LocationState>() as unknown as ILocationWithQuery;
  const { state } = location;

  const getDefaultFirstSegment = useCallback((): TFirstSegment => {
    if (categoryType === 'keyword_topic') {
      return 'keyword_topic';
    }
    return 'keyword';
  }, [categoryType]);

  // Segment 状态
  const [firstSegment, setFirstSegment] = useState<TFirstSegment>(
    initialSecondSegment ? 'subscription' : getDefaultFirstSegment(),
  );

  const [owner, setOwner] = useState<TReportOwner>(
    initialOwner || state?.owner || SAVED_LIST_DEFAULT_REPORT_OWNER,
  );

  // 监听 initialSecondSegment / initialOwner / location.state 变化，同步 Segment 状态
  useEffect(() => {
    if (state) {
      const { isSubscription, source, owner: stateOwner } = state || {};
      if (stateOwner) {
        setOwner(stateOwner);
      }
      if (isSubscription) {
        setFirstSegment('subscription');
      } else if (source) {
        setFirstSegment(source as TFirstSegment);
      }
      return;
    }

    if (initialOwner) {
      setOwner(initialOwner);
    }

    if (initialSecondSegment) {
      setFirstSegment('subscription');
    }
  }, [initialSecondSegment, initialOwner, state, getDefaultFirstSegment]);

  const handleOwnerChange = (value: TReportOwner) => {
    if (value === owner) return;
    buriedBtnPoint('ReportOwner', value);
    setOwner(value);
    setFirstSegment(getDefaultFirstSegment());
  };

  // 计算实际的 category（优先使用 Segment 选择）
  const actualCategory = useMemo(() => {
    if (!showSegment) {
      return categoryType;
    }
    return firstSegment; // 临时任务使用第一层的选择
  }, [showSegment, firstSegment, categoryType]);

  // 判断是否为订阅任务
  const isSubscription = showSegment && firstSegment === 'subscription';

  // 构建过滤参数
  const filter = useMemo(() => {
    if (!gameInfo?.edition_unified_id) {
      return {};
    }

    return {
      edition_unified_ids: [gameInfo.edition_unified_id],
      owner,
    };
  }, [gameInfo, owner]);

  const { loading, deleteToggleState } = useAiTaskTabInfo({
    filter,
  });

  // 第一层 Segment 选项配置
  const firstSegmentOptions = useMemo(() => {
    const list = [
      {
        value: 'subscription',
        text: formatMessage({ id: 'Subscriptions' }),
      },
    ];

    if (categoryType === 'keyword') {
      list.unshift({
        value: 'keyword',
        text: formatMessage({ id: 'Keyword' }),
      });
    } else {
      list.unshift({
        value: 'keyword_topic',
        text: formatMessage({ id: 'Natural Language Search' }),
      });
    }

    return list;
  }, [categoryType, formatMessage]);

  return (
    <>
      <div className="dbo-saved-analysis-list">
        <CardCommon
          className="dbo-saved-analysis-list-table"
          loading={loading}
          loadingHeight={500}
        >
          <>
            <div className="dbo-saved-analysis-list-segments">
              <ReportOwnerSegment
                showAll
                value={owner}
                onChange={handleOwnerChange}
              />
              {showSegment && (
                <Segment
                  className="segment-font12"
                  value={firstSegment}
                  onChange={(value) => setFirstSegment(value as TFirstSegment)}
                  options={firstSegmentOptions}
                />
              )}
            </div>
            {/* 根据是否为订阅任务渲染不同的表格组件 */}
            {isSubscription ? (
              <SubscriptionsTable
                filter={filter}
                keys={[
                  'analysis_name',
                  'last_report_generated_time',
                  'creator',
                  'status',
                  'permission',
                  'multi_action',
                ]}
                gameInfoMap={{ [gameInfo?.edition_unified_id]: wholeGameInfo }}
                deleteToggleState={deleteToggleState}
                tab={categoryType === 'keyword' ? 'keyword' : 'topic'}
              />
            ) : (
              <SummaryListTable
                filter={filter}
                source={actualCategory as TabType}
                keys={[
                  'analysis_name',
                  'category',
                  'create_time',
                  'creator',
                  'status',
                  'permission',
                  'action',
                ]}
                gameInfoMap={{ [gameInfo?.edition_unified_id]: wholeGameInfo }}
                deleteToggleState={deleteToggleState}
              />
            )}
          </>
        </CardCommon>
      </div>
    </>
  );
};

export default SavedAnalysisList;
